/**
 * Route manifest for the six core daily-use pages (S2).
 *
 * `src/routes.tsx` generates one route per `PAGE_REGISTRY` entry and renders
 * `PagePlaceholder` for anything not yet ported. This file hands the router the
 * REAL elements for `dashboard`, `records`, `income`, `expenses` and `accounts`
 * (list + detail). The orchestrator splices `coreRoutes` into the authenticated
 * layout's `children`, where it must come BEFORE the registry-generated
 * placeholders so these five keys resolve to the ported page rather than the
 * placeholder — react-router picks the best match, and `accounts` here is a
 * more specific match than the registry's `accounts/*`.
 *
 * SAFE-04: this file adds NO route that the registry does not already define.
 * Every path below is an existing `PAGE_REGISTRY.matchPrefix`, so each page
 * keeps the identical `RequireAuth` + permission gate it has today, and the
 * navbar stays the same fixed 7-entry set (income and expenses remain
 * `hideFromNav`, reached from inside Records exactly as before).
 *
 * Paths are relative (no leading slash) because these are children of the
 * layout route, matching the shape `routes.tsx` already uses.
 *
 * S3: every element below is `React.lazy` + a shared `Suspense` fallback
 * (`RouteSkeleton`) instead of a static import — this is the biggest single
 * slice of the pre-split ~1.5 MB main chunk, since these six pages (plus the
 * statement-import pair) previously loaded on EVERY first visit regardless of
 * which one the user actually opened. `dashboard` is still the very next
 * chunk requested after login (see `useIdlePrefetch` in `lib/prefetch.ts`),
 * so splitting it costs no perceptible delay while still keeping it out of
 * the bytes a signed-out visitor pays for on `/login`.
 */
import { Suspense, type ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import { lazyNamed, RouteSkeleton } from "@/lib/prefetch";

const DashboardPage = lazyNamed(() => import("@/pages/DashboardPage"), "DashboardPage");
const RecordsPage = lazyNamed(() => import("@/pages/records/RecordsPage"), "RecordsPage");
const IncomePage = lazyNamed(() => import("@/pages/income/IncomePage"), "IncomePage");
const ExpensesPage = lazyNamed(() => import("@/pages/expenses/ExpensesPage"), "ExpensesPage");
const AccountsPage = lazyNamed(() => import("@/pages/accounts/AccountsPage"), "AccountsPage");
const AccountDetailPage = lazyNamed(
  () => import("@/pages/accounts/AccountDetailPage"),
  "AccountDetailPage",
);
// V5-24/V5-25 — bank statement import. It lives UNDER `records`, not beside
// it: the navbar is a fixed seven-entry set, the registry is the only thing
// that gates a route, and `findPageByPath("/records/statements")` resolves to
// the `records` entry by prefix. So these two paths inherit the exact
// permission check `/records` already has, and add no registry row.
// Wave-3 rules engine (W6/W7): mounted under `records` for the same reason as
// the statement importer directly below — prefix inheritance of the records
// permission, no new registry row, navbar untouched.
const RulesPage = lazyNamed(
  () => import("@/pages/rules/RulesPage"),
  "RulesPage",
);
const StatementImportPage = lazyNamed(
  () => import("@/pages/statements/StatementImportPage"),
  "StatementImportPage",
);
const StatementImportReviewPage = lazyNamed(
  () => import("@/pages/statements/StatementImportReviewPage"),
  "StatementImportReviewPage",
);

/** One Suspense boundary per route, so one slow chunk never blanks its siblings. */
function s(element: ReactNode): ReactNode {
  return <Suspense fallback={<RouteSkeleton />}>{element}</Suspense>;
}

/** Registry keys this manifest implements — the orchestrator drops the
 *  placeholder for each of these when splicing. */
export const CORE_PAGE_KEYS = [
  "dashboard",
  "records",
  "income",
  "expenses",
  "accounts",
] as const;

export const coreRoutes: RouteObject[] = [
  { path: "dashboard/*", element: s(<DashboardPage />) },
  // Declared before the `records/*` splat for readability; react-router ranks
  // by specificity rather than array order, so the static segments win either
  // way and `/records` itself still reaches `RecordsPage`.
  { path: "records/rules", element: s(<RulesPage />) },
  { path: "records/statements", element: s(<StatementImportPage />) },
  { path: "records/statements/:importId", element: s(<StatementImportReviewPage />) },
  { path: "records/*", element: s(<RecordsPage />) },
  { path: "income/*", element: s(<IncomePage />) },
  { path: "expenses/*", element: s(<ExpensesPage />) },
  // Two flat entries rather than a splat: `/accounts` and `/accounts/:id` are
  // exactly the two files Next.js had, and a `/*` path cannot carry children.
  { path: "accounts", element: s(<AccountsPage />) },
  { path: "accounts/:id", element: s(<AccountDetailPage />) },
];

export default coreRoutes;
